/**
 * MoldeOne - Servico de comunicacao com C#
 *
 * Acoes atuais:
 * - moldeone:action        -> preparacao do template
 * - moldeone:gerar-grade   -> nova geracao consolidada
 */

import { corelCommunication } from '../../../lib/corel/communication';
import type {
  ArtSelectionResponse,
  ApplyArtRequest,
  ApplyArtUnusedInput,
  ApplyArtValidationResult,
  MoldeCollectResponse,
  EscanearPaginaResponse,
  GerarGradeRequest,
  GerarGradeResponse,
  LerSelecaoResponse,
  LerPecaResponse,
  ListarMarcadosResponse,
  MoldeMetadata,
  SalvarMoldeResponse,
  SalvarDadosMoldeResponse,
  ValidarTemplateResponse,
} from './types';

function sendCorelAction<T>(
  action: string,
  data: Record<string, unknown> = {},
  timeoutMs = 10000
): Promise<T> {
  return new Promise((resolve, reject) => {
    corelCommunication.sendActionGuarded(
      action,
      data,
      (response: any) => {
        if (response?.status === 'ok' || response?.status === 'success') {
          resolve(response.result as T);
        } else {
          reject(new Error(response?.message || `Erro na action: ${action}`));
        }
      },
      timeoutMs
    );
  });
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isActionInProgressError(error: unknown) {
  return error instanceof Error && error.message.includes('Esta ação já está em andamento');
}

async function sendMoldeOneAction<T>(operacao: string, data: Record<string, unknown> = {}): Promise<T> {
  let lastError: unknown = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await sendCorelAction<T>('moldeone:action', { operacao, ...data }, 10000);
    } catch (error) {
      lastError = error;
      if (!isActionInProgressError(error) || attempt === 2) break;
      await delay(120 * (attempt + 1));
    }
  }

  throw lastError instanceof Error ? lastError : new Error(`Erro na operacao MoldeOne: ${operacao}`);
}

function normalizeArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizeInventoryItem(item: any) {
  return {
    staticId: Number(item?.staticId ?? item?.StaticId ?? 0),
    shapeName: item?.shapeName ?? item?.ShapeName ?? '',
    shapeType: item?.shapeType ?? item?.ShapeType ?? '',
    layerName: item?.layerName ?? item?.LayerName ?? '',
    isText: Boolean(item?.isText ?? item?.IsText),
    state: item?.state ?? item?.State ?? 'nao_mapeado',
    identificador: item?.identificador ?? item?.Identificador ?? '',
    peca: item?.peca ?? item?.Peca ?? '',
    parte: item?.parte ?? item?.Parte ?? '',
    lado: item?.lado ?? item?.Lado ?? '',
    index: item?.index ?? item?.Index ?? '',
    escala: item?.escala ?? item?.Escala ?? '',
    placeholder: item?.placeholder ?? item?.Placeholder ?? '',
    textValue: item?.textValue ?? item?.TextValue ?? '',
    detectedTextTokens: normalizeArray<string>(item?.detectedTextTokens ?? item?.DetectedTextTokens),
    textWarnings: normalizeArray<string>(item?.textWarnings ?? item?.TextWarnings),
    problems: normalizeArray<string>(item?.problems ?? item?.Problems),
  };
}

function normalizeMoldeMetadata(raw: any): MoldeMetadata {
  return {
    familiaTamanho: raw?.familiaTamanho ?? raw?.FamiliaTamanho ?? '',
    tamanho: raw?.tamanho ?? raw?.Tamanho ?? '',
    custom: Boolean(raw?.custom ?? raw?.Custom),
    paginaCanonica: raw?.paginaCanonica ?? raw?.PaginaCanonica ?? '',
    sincronizadoComPagina: Boolean(raw?.sincronizadoComPagina ?? raw?.SincronizadoComPagina),
  };
}

function normalizeUnusedInput(raw: any): ApplyArtUnusedInput {
  return {
    kind: raw?.kind ?? raw?.Kind ?? '',
    key: raw?.key ?? raw?.Key ?? '',
    index: raw?.index ?? raw?.Index ?? null,
    label: raw?.label ?? raw?.Label ?? '',
  };
}

function normalizeApplyArtValidationResult(raw: any): ApplyArtValidationResult {
  const resumo = raw?.resumo ?? raw?.Resumo ?? {};

  return {
    status: raw?.status ?? raw?.Status ?? 'invalido',
    moldeRef: {
      arquivo: raw?.moldeRef?.arquivo ?? raw?.MoldeRef?.Arquivo ?? '',
      pagina: raw?.moldeRef?.pagina ?? raw?.MoldeRef?.Pagina ?? '',
    },
    molde: raw?.molde || raw?.Molde ? normalizeMoldeMetadata(raw?.molde ?? raw?.Molde) : undefined,
    resumo: {
      structuralExpected: Number(resumo?.structuralExpected ?? resumo?.StructuralExpected ?? 0),
      structuralMatched: Number(resumo?.structuralMatched ?? resumo?.StructuralMatched ?? 0),
      structuralMissing: Number(resumo?.structuralMissing ?? resumo?.StructuralMissing ?? 0),
      emblemsExpected: Number(resumo?.emblemsExpected ?? resumo?.EmblemsExpected ?? 0),
      emblemsMatched: Number(resumo?.emblemsMatched ?? resumo?.EmblemsMatched ?? 0),
      emblemsMissing: Number(resumo?.emblemsMissing ?? resumo?.EmblemsMissing ?? 0),
      labelsExpected: Number(resumo?.labelsExpected ?? resumo?.LabelsExpected ?? 0),
      labelsMatched: Number(resumo?.labelsMatched ?? resumo?.LabelsMatched ?? 0),
      labelsMissing: Number(resumo?.labelsMissing ?? resumo?.LabelsMissing ?? 0),
      textExpected: Number(resumo?.textExpected ?? resumo?.TextExpected ?? 0),
      textMatched: Number(resumo?.textMatched ?? resumo?.TextMatched ?? 0),
      textMissing: Number(resumo?.textMissing ?? resumo?.TextMissing ?? 0),
      unusedInputs: Number(resumo?.unusedInputs ?? resumo?.UnusedInputs ?? 0),
      invalidSlots: Number(resumo?.invalidSlots ?? resumo?.InvalidSlots ?? 0),
    },
    matchedStructural: normalizeArray<string>(raw?.matchedStructural ?? raw?.MatchedStructural),
    missingStructural: normalizeArray<string>(raw?.missingStructural ?? raw?.MissingStructural),
    matchedEmblems: normalizeArray<number>(raw?.matchedEmblems ?? raw?.MatchedEmblems),
    missingEmblems: normalizeArray<number>(raw?.missingEmblems ?? raw?.MissingEmblems),
    matchedLabels: normalizeArray<number>(raw?.matchedLabels ?? raw?.MatchedLabels),
    missingLabels: normalizeArray<number>(raw?.missingLabels ?? raw?.MissingLabels),
    matchedTexts: normalizeArray<string>(raw?.matchedTexts ?? raw?.MatchedTexts),
    missingTexts: normalizeArray<string>(raw?.missingTexts ?? raw?.MissingTexts),
    textWarnings: normalizeArray<string>(raw?.textWarnings ?? raw?.TextWarnings),
    unusedInputs: normalizeArray<any>(raw?.unusedInputs ?? raw?.UnusedInputs).map(normalizeUnusedInput),
    invalidSlots: normalizeArray<string>(raw?.invalidSlots ?? raw?.InvalidSlots),
    warnings: normalizeArray<string>(raw?.warnings ?? raw?.Warnings),
    errors: normalizeArray<string>(raw?.errors ?? raw?.Errors),
  };
}

function normalizeCollectResponse(raw: any): MoldeCollectResponse {
  const documento = raw?.documento ?? raw?.Documento ?? {};
  const pagina = raw?.pagina ?? raw?.Pagina ?? {};
  const resumo = raw?.resumo ?? raw?.Resumo ?? {};
  const objetos = normalizeArray<any>(raw?.objetos ?? raw?.Objetos).map((item) => ({
    ...normalizeInventoryItem(item),
    widthMm: Number(item?.widthMm ?? item?.WidthMm ?? 0),
    heightMm: Number(item?.heightMm ?? item?.HeightMm ?? 0),
    centerXmm: Number(item?.centerXmm ?? item?.CenterXmm ?? 0),
    centerYmm: Number(item?.centerYmm ?? item?.CenterYmm ?? 0),
    pageName: item?.pageName ?? item?.PageName ?? '',
    pageIndex: Number(item?.pageIndex ?? item?.PageIndex ?? 0),
    zOrder: item?.zOrder ?? item?.ZOrder ?? null,
    source: item?.source ?? item?.Source ?? 'direto',
    parentGroupName: item?.parentGroupName ?? item?.ParentGroupName ?? '',
    previewBox: {
      widthMm: Number(item?.previewBox?.widthMm ?? item?.PreviewBox?.WidthMm ?? item?.widthMm ?? item?.WidthMm ?? 0),
      heightMm: Number(item?.previewBox?.heightMm ?? item?.PreviewBox?.HeightMm ?? item?.heightMm ?? item?.HeightMm ?? 0),
      aspectRatio: Number(
        item?.previewBox?.aspectRatio ??
          item?.PreviewBox?.AspectRatio ??
          (Number(item?.heightMm ?? item?.HeightMm ?? 0) > 0
            ? Number(item?.widthMm ?? item?.WidthMm ?? 0) / Number(item?.heightMm ?? item?.HeightMm ?? 1)
            : 1)
      ),
    },
    previewCurve: item?.previewCurve || item?.PreviewCurve
      ? {
          width: Number(item?.previewCurve?.width ?? item?.PreviewCurve?.Width ?? 0),
          height: Number(item?.previewCurve?.height ?? item?.PreviewCurve?.Height ?? 0),
          maxLength: Number(item?.previewCurve?.maxLength ?? item?.PreviewCurve?.MaxLength ?? 0),
          paths: normalizeArray<any>(item?.previewCurve?.paths ?? item?.PreviewCurve?.Paths).map((path) => ({
            closed: Boolean(path?.closed ?? path?.Closed),
            segments: normalizeArray<any>(path?.segments ?? path?.Segments).map((segment) => ({
              snp: {
                x: Number(segment?.snp?.x ?? segment?.Snp?.X ?? 0),
                y: Number(segment?.snp?.y ?? segment?.Snp?.Y ?? 0),
              },
              scp: {
                x: Number(segment?.scp?.x ?? segment?.Scp?.X ?? 0),
                y: Number(segment?.scp?.y ?? segment?.Scp?.Y ?? 0),
              },
              ecp: {
                x: Number(segment?.ecp?.x ?? segment?.Ecp?.X ?? 0),
                y: Number(segment?.ecp?.y ?? segment?.Ecp?.Y ?? 0),
              },
            })),
          })),
        }
      : null,
  }));

  return {
    documento: {
      nome: documento?.nome ?? documento?.Nome ?? '',
      caminho: documento?.caminho ?? documento?.Caminho ?? '',
      salvoNoDisco: Boolean(documento?.salvoNoDisco ?? documento?.SalvoNoDisco),
    },
    pagina: {
      nome: pagina?.nome ?? pagina?.Nome ?? '',
      indice: Number(pagina?.indice ?? pagina?.Indice ?? 0),
    },
    resumo: {
      total: Number(resumo?.total ?? resumo?.Total ?? objetos.length),
      mapeados: Number(resumo?.mapeados ?? resumo?.Mapeados ?? 0),
      pendentes: Number(resumo?.pendentes ?? resumo?.Pendentes ?? 0),
      placeholders: Number(resumo?.placeholders ?? resumo?.Placeholders ?? 0),
      conflitos: Number(resumo?.conflitos ?? resumo?.Conflitos ?? 0),
    },
    molde: raw?.molde || raw?.Molde ? normalizeMoldeMetadata(raw?.molde ?? raw?.Molde) : undefined,
    objetos,
    warnings: normalizeArray<string>(raw?.warnings ?? raw?.Warnings),
  };
}

function normalizeArtSelectionResponse(raw: any): ArtSelectionResponse {
  const base = normalizeCollectResponse(raw);
  const resumo = raw?.resumo ?? raw?.Resumo ?? {};

  return {
    documento: base.documento,
    pagina: base.pagina,
    molde: base.molde,
    warnings: base.warnings,
    resumo: {
      total: Number(resumo?.total ?? resumo?.Total ?? base.objetos.length),
      marcados: Number(resumo?.marcados ?? resumo?.Marcados ?? 0),
      pendentes: Number(resumo?.pendentes ?? resumo?.Pendentes ?? 0),
      conflitos: Number(resumo?.conflitos ?? resumo?.Conflitos ?? 0),
    },
    objetos: base.objetos.map((item: any) => ({
      ...item,
      state: (item?.state ?? item?.State ?? 'pendente') as ArtSelectionResponse['objetos'][number]['state'],
      slotId: item?.slotId ?? item?.SlotId ?? '',
      slotKind: item?.slotKind ?? item?.SlotKind ?? '',
    })),
  };
}

function normalizeScanResponse(raw: any): EscanearPaginaResponse {
  const pagina = raw?.pagina ?? raw?.Pagina ?? {};
  const resumo = raw?.resumo ?? raw?.Resumo ?? {};
  const checklist = normalizeArray<any>(raw?.checklist ?? raw?.Checklist).map((item) => ({
    key: item?.key ?? item?.Key ?? '',
    label: item?.label ?? item?.Label ?? '',
    required: Boolean(item?.required ?? item?.Required),
    status: item?.status ?? item?.Status ?? 'faltando',
    matchedStaticIds: normalizeArray<number>(item?.matchedStaticIds ?? item?.MatchedStaticIds),
  }));
  const candidatos = normalizeArray<any>(raw?.candidatos ?? raw?.Candidatos).map(normalizeInventoryItem);

  return {
    pagina: {
      nome: pagina?.nome ?? pagina?.Nome ?? '',
      indice: Number(pagina?.indice ?? pagina?.Indice ?? 0),
      documentoNome: pagina?.documentoNome ?? pagina?.DocumentoNome ?? '',
      documentoCaminho: pagina?.documentoCaminho ?? pagina?.DocumentoCaminho ?? '',
      salvoNoDisco: Boolean(pagina?.salvoNoDisco ?? pagina?.SalvoNoDisco),
    },
    resumo: {
      totalCandidatos: Number(resumo?.totalCandidatos ?? resumo?.TotalCandidatos ?? candidatos.length),
      mapeados: Number(resumo?.mapeados ?? resumo?.Mapeados ?? 0),
      pendentes: Number(resumo?.pendentes ?? resumo?.Pendentes ?? 0),
      placeholders: Number(resumo?.placeholders ?? resumo?.Placeholders ?? 0),
      conflitos: Number(resumo?.conflitos ?? resumo?.Conflitos ?? 0),
    },
    molde: raw?.molde || raw?.Molde ? normalizeMoldeMetadata(raw?.molde ?? raw?.Molde) : undefined,
    checklist,
    candidatos,
    warnings: normalizeArray<string>(raw?.warnings ?? raw?.Warnings),
  };
}

function normalizeSelectionResponse(raw: any): LerSelecaoResponse {
  const selecao = raw?.selecao ?? raw?.Selecao ?? {};
  const resumo = raw?.resumo ?? raw?.Resumo ?? {};
  const itens = normalizeArray<any>(raw?.itens ?? raw?.Itens).map((item) => ({
    ...normalizeInventoryItem(item),
    source: item?.source ?? item?.Source ?? 'direto',
    parentGroupName: item?.parentGroupName ?? item?.ParentGroupName ?? '',
  }));
  const ignorados = normalizeArray<any>(raw?.ignorados ?? raw?.Ignorados).map((item) => ({
    staticId: Number(item?.staticId ?? item?.StaticId ?? 0),
    shapeName: item?.shapeName ?? item?.ShapeName ?? '',
    shapeType: item?.shapeType ?? item?.ShapeType ?? '',
    reason: item?.reason ?? item?.Reason ?? 'nao_elegivel',
  }));

  return {
    selecao: {
      totalSelecionadosCorel: Number(selecao?.totalSelecionadosCorel ?? selecao?.TotalSelecionadosCorel ?? 0),
      totalItensRetornados: Number(selecao?.totalItensRetornados ?? selecao?.TotalItensRetornados ?? itens.length),
      totalIgnorados: Number(selecao?.totalIgnorados ?? selecao?.TotalIgnorados ?? ignorados.length),
      paginaNome: selecao?.paginaNome ?? selecao?.PaginaNome ?? '',
      paginaIndice: Number(selecao?.paginaIndice ?? selecao?.PaginaIndice ?? 0),
      documentoNome: selecao?.documentoNome ?? selecao?.DocumentoNome ?? '',
      documentoCaminho: selecao?.documentoCaminho ?? selecao?.DocumentoCaminho ?? '',
      salvoNoDisco: Boolean(selecao?.salvoNoDisco ?? selecao?.SalvoNoDisco),
    },
    resumo: {
      mapeados: Number(resumo?.mapeados ?? resumo?.Mapeados ?? 0),
      naoMapeados: Number(resumo?.naoMapeados ?? resumo?.NaoMapeados ?? 0),
      placeholders: Number(resumo?.placeholders ?? resumo?.Placeholders ?? 0),
      conflitos: Number(resumo?.conflitos ?? resumo?.Conflitos ?? 0),
    },
    itens,
    ignorados,
    warnings: normalizeArray<string>(raw?.warnings ?? raw?.Warnings),
  };
}

/** Marca o shape selecionado com as Properties MO.* */
export function marcarPeca(params: {
  peca: string;
  parte?: string;
  lado?: string;
  index?: string;
  escala?: string;
  zona?: string;
  placeholder?: string;
}) {
  return sendMoldeOneAction<{ marcado: boolean; identificador: string; shapeName: string; staticId: number }>(
    'marcar-peca',
    params
  );
}

/** Le a marcacao do shape selecionado */
export function lerPeca() {
  return sendMoldeOneAction<LerPecaResponse>('ler-peca');
}

/** Remove marcacao do shape selecionado */
export function limparPeca() {
  return sendMoldeOneAction<{ limpo: boolean; shapeName: string; marcacaoAnterior: string }>(
    'limpar-peca'
  );
}

/** Lista todos os shapes marcados no documento */
export function listarMarcados() {
  return sendMoldeOneAction<ListarMarcadosResponse>('listar-marcados');
}

/** Escaneia a pagina ativa e devolve um snapshot pronto para a UI do Editar Molde */
export async function escanearPagina() {
  const raw = await sendMoldeOneAction<any>('escanear-pagina');
  return normalizeScanResponse(raw);
}

/** Le todos os shapes atualmente selecionados e devolve um inventario pronto para a UI */
export async function lerSelecao() {
  const raw = await sendMoldeOneAction<any>('ler-selecao');
  return normalizeSelectionResponse(raw);
}

/** Coleta os objetos da seleção atual com dados visuais prontos para cards do MoldeOne v3 */
export async function coletarObjetos() {
  const raw = await sendMoldeOneAction<any>('coletar-objetos');
  return normalizeCollectResponse(raw);
}

export async function coletarArte() {
  const raw = await sendMoldeOneAction<any>('coletar-arte');
  return normalizeArtSelectionResponse(raw);
}

export function marcarArte(params: { staticId: string | number; slotId: string }) {
  return sendMoldeOneAction<{
    marcado: boolean;
    identificador: string;
    slotId: string;
    shapeName: string;
    staticId: number;
  }>('marcar-arte', {
    staticId: String(params.staticId),
    slotId: params.slotId,
  });
}

export function limparArte(params: { staticId: string | number }) {
  return sendMoldeOneAction<{ limpo: boolean; staticId: string }>('limpar-arte', {
    staticId: String(params.staticId),
  });
}

/** Valida se o template tem as pecas obrigatorias */
export function validarTemplate(tipoMolde: string) {
  return sendMoldeOneAction<ValidarTemplateResponse>('validar', { tipoMolde });
}

/** Salva o molde/documento atual no mesmo arquivo em disco */
export function salvarMoldeAtual() {
  return sendMoldeOneAction<SalvarMoldeResponse>('salvar-documento-atual');
}

/** Finaliza o molde, sincroniza o tamanho com a pagina atual e salva o .cdr */
export async function finalizarESalvarMoldeAtual() {
  const raw = await sendMoldeOneAction<any>('finalizar-e-salvar');
  return {
    salvo: Boolean(raw?.salvo ?? raw?.Salvo),
    paginaRenomeada: Boolean(raw?.paginaRenomeada ?? raw?.PaginaRenomeada),
    paginaAnterior: raw?.paginaAnterior ?? raw?.PaginaAnterior ?? '',
    paginaAtual: raw?.paginaAtual ?? raw?.PaginaAtual ?? '',
    nomeArquivo: raw?.nomeArquivo ?? raw?.NomeArquivo ?? '',
    caminhoCompleto: raw?.caminhoCompleto ?? raw?.CaminhoCompleto ?? '',
    diretorio: raw?.diretorio ?? raw?.Diretorio ?? '',
    salvoNoDisco: Boolean(raw?.salvoNoDisco ?? raw?.SalvoNoDisco),
    molde: raw?.molde || raw?.Molde ? normalizeMoldeMetadata(raw?.molde ?? raw?.Molde) : undefined,
  } as SalvarMoldeResponse;
}

export async function lerDadosMolde() {
  const raw = await sendMoldeOneAction<any>('ler-dados-molde');
  return normalizeMoldeMetadata(raw);
}

export async function salvarDadosMolde(payload: { familiaTamanho: string; tamanho: string }) {
  const raw = await sendMoldeOneAction<any>('salvar-dados-molde', payload);
  return normalizeMoldeMetadata(raw) as SalvarDadosMoldeResponse;
}

export async function validarCasamentoArte(payload: ApplyArtRequest) {
  const raw = await sendMoldeOneAction<any>('validar-casamento-arte', payload);
  return normalizeApplyArtValidationResult(raw);
}

/** Dispara a nova geracao consolidada */
export function gerarGrade(payload: GerarGradeRequest) {
  return sendCorelAction<GerarGradeResponse>('moldeone:gerar-grade', payload, 60000);
}
